import { useEffect } from 'react'
import { DateTime } from 'luxon'

import { Accordion, Alert, Link, Loader, Separator, Text } from '@/ui'
import { useFetchResponse, useReselector } from '@/utils/sharedHooks'
import {
  FETCH_QA_FEATURES_KEY,
  fetchQAFeaturesIfNeeded,
  Pathway,
} from './qaFeatures.slice'
import { getFetchError, getIsFetching } from '@/reducers/fetch'
import { selectQAFeatures } from './qaFeatures.selectors'
import { selectQAUsersForFeature } from './qaUsers.selectors'
import { FETCH_QA_USERS_KEY, fetchQAUsers } from './qaUsers.slice'
import UserTable from './components/UserTable'
import PathwayTable from './components/PathwayTable'
import { useAppDispatch } from '@/utils/typeHelpers'
import { TypographyStyles } from '@/styles'
import {
  fetchAllStripeProducts,
  StripeProduct,
} from '@/actions/settings/billingActions'

const AccordionTitle = ({
  name,
  createdAt,
  description,
  linearCode,
}: {
  name: string
  createdAt: string
  description: string
  linearCode: string
}) => (
  <>
    <Text variant={TypographyStyles.H2}>{name}</Text>
    <Text>
      <Link newPage href={`https://linear.app/heard/issue/${linearCode}`}>
        {linearCode}
      </Link>
      {` - ${description}`}
    </Text>
    <Text>{DateTime.fromISO(createdAt).toRelative()}</Text>
  </>
)

const AccordionContent = ({
  pathways,
  stripeProducts,
}: {
  pathways: Pathway[]
  stripeProducts?: StripeProduct[]
}) => {
  const users = useReselector(selectQAUsersForFeature, pathways)
  return (
    <>
      <div>
        <Text variant={TypographyStyles.H3}>
          Available Paths - {pathways.length}
        </Text>
        <PathwayTable pathways={pathways} stripeProducts={stripeProducts} />
      </div>
      <div>
        <Text variant={TypographyStyles.H3}>QA Users - {users.length}</Text>
        <UserTable users={users} />
      </div>
    </>
  )
}

const QAPanel = () => {
  const dispatch = useAppDispatch()
  const stripeProducts = useFetchResponse(fetchAllStripeProducts)

  useEffect(() => {
    dispatch(fetchQAFeaturesIfNeeded())
    dispatch(fetchQAUsers())
  }, [dispatch])

  const fetchFeaturesError = useReselector(getFetchError, FETCH_QA_FEATURES_KEY)
  const fetchUsersError = useReselector(getFetchError, FETCH_QA_FEATURES_KEY)
  const features = useReselector(selectQAFeatures)

  const qaFeaturesFetching = useReselector(getIsFetching, FETCH_QA_FEATURES_KEY)
  const qaUsersFetching = useReselector(getIsFetching, FETCH_QA_USERS_KEY)

  return (
    <>
      <Text variant={TypographyStyles.H2}>Available features</Text>
      <Separator />
      <Loader active={qaFeaturesFetching} />
      <Loader active={qaUsersFetching} />
      {(fetchFeaturesError || fetchUsersError) && (
        <Alert
          variant="error"
          title="Something went wrong"
          description="There was an error loading features or users. Please try refreshing the page"
        />
      )}
      <Accordion type="single" collapsible variant="text">
        {features?.map((feature) => (
          <Accordion.Item key={feature.linearCode} value={feature.linearCode}>
            <Accordion.Trigger>
              <AccordionTitle {...feature} />
            </Accordion.Trigger>
            <Accordion.Content>
              <AccordionContent
                pathways={feature.pathways}
                stripeProducts={stripeProducts}
              />
            </Accordion.Content>
          </Accordion.Item>
        ))}
      </Accordion>
    </>
  )
}

export default QAPanel
